import React, { useCallback, useEffect, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import {
  ClipboardList, ChevronDown, ChevronRight, Loader2, Download, Upload,
  CheckCircle2, CircleAlert, Eye, RotateCcw, ShieldCheck,
} from "lucide-react";

const STATUS_CHIP = {
  "needs-input": "bg-amber-100 text-amber-700 border-amber-300",
  "needs-review": "bg-amber-100 text-amber-700 border-amber-300",
  uploaded: "bg-emerald-100 text-emerald-700 border-emerald-300",
  reviewed: "bg-emerald-100 text-emerald-700 border-emerald-300",
  "confirm-in-sam": "bg-zinc-100 text-zinc-600 border-zinc-200",
};
const STATUS_LABEL = {
  "needs-input": "Needs input", "needs-review": "Needs review", uploaded: "Uploaded & merged",
  reviewed: "Reviewed", "confirm-in-sam": "Confirm in SAM",
};

/* A single manual submittable row: download blank · upload completed · review · remove. */
function FormRow({ dealId, form, onChanged }) {
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const done = form.status === "uploaded" || form.status === "reviewed";

  const afterMutate = () => { onChanged && onChanged(); window.dispatchEvent(new Event("ironclad-refresh")); };

  const upload = async (fileEl) => {
    const file = fileEl.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/bids/${dealId}/forms/${form.form_key}/reupload`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${form.title} completed & merged into the package.`);
      afterMutate();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed.");
    } finally { setBusy(false); if (fileEl) fileEl.value = ""; }
  };

  const review = async () => {
    setBusy(true);
    try {
      await api.post(`/bids/${dealId}/forms/${form.form_key}/review`, { note: "Reviewed and accepted." });
      toast.success(`${form.title} reviewed & accepted.`);
      afterMutate();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Review failed.");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/bids/${dealId}/forms/${form.form_key}/upload`);
      toast.success(`${form.title} completion removed — its blockers are restored.`);
      afterMutate();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed.");
    } finally { setBusy(false); }
  };

  const Icon = form.action === "confirm" ? ShieldCheck : done ? CheckCircle2 : CircleAlert;
  const iconCls = form.action === "confirm" ? "text-zinc-400" : done ? "text-emerald-500" : "text-amber-500";

  return (
    <li className="flex flex-col gap-2 border border-zinc-200 rounded-lg p-3" data-testid={`manual-form-${form.form_key}`}>
      <div className="flex items-start gap-2">
        <Icon className={`h-4 w-4 shrink-0 mt-0.5 ${iconCls}`} />
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-medium text-zinc-800 truncate" title={form.title}>{form.title}</div>
          {form.completion?.filename && (
            <div className="text-[11px] text-emerald-600 truncate" data-testid={`manual-form-file-${form.form_key}`}>
              {form.completion.filename} · {form.completion.by}
            </div>
          )}
          {form.action === "confirm" && (
            <div className="text-[11px] text-zinc-500">Genuine representation — confirm on the page / in SAM. Never machine-completed.</div>
          )}
        </div>
        <span className={`shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded border ${STATUS_CHIP[form.status] || STATUS_CHIP["needs-input"]}`}
          data-testid={`manual-form-status-${form.form_key}`}>
          {STATUS_LABEL[form.status] || form.status}
        </span>
      </div>

      {form.action !== "confirm" && (
        <div className="flex flex-wrap items-center gap-3 pl-6">
          <a href={`${API}/bids/${dealId}/forms/${form.form_key}/blank`} target="_blank" rel="noreferrer"
            className="text-[11px] text-zinc-600 underline hover:text-zinc-900 flex items-center gap-1"
            data-testid={`manual-form-blank-${form.form_key}`}>
            <Download className="h-3.5 w-3.5" /> Download blank
          </a>

          <label className="text-[11px] text-zinc-700 underline hover:text-zinc-900 cursor-pointer flex items-center gap-1"
            data-testid={`manual-form-upload-${form.form_key}`}>
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
            {done ? "Replace completed PDF" : "Upload completed PDF"}
            <input ref={fileRef} type="file" accept="application/pdf" className="hidden"
              onChange={(e) => upload(e.target)} disabled={busy} />
          </label>

          {form.action === "review" && form.status === "needs-review" && (
            <button onClick={review} disabled={busy}
              className="text-[11px] text-amber-700 underline hover:text-amber-900 flex items-center gap-1 disabled:opacity-50"
              data-testid={`manual-form-review-${form.form_key}`}>
              <Eye className="h-3.5 w-3.5" /> Mark reviewed &amp; accept
            </button>
          )}

          {done && (
            <button onClick={remove} disabled={busy}
              className="text-[11px] text-zinc-500 underline hover:text-red-600 flex items-center gap-1 disabled:opacity-50"
              data-testid={`manual-form-remove-${form.form_key}`}>
              <RotateCcw className="h-3.5 w-3.5" /> Remove
            </button>
          )}
        </div>
      )}
    </li>
  );
}

/* Dir 95 — MANUAL-FORM COMPLETION PANEL: every human-input submittable an operator completes (upload),
   an auto-filled form to review, or a genuine rep to confirm in SAM. One screen, deterministic gate. */
export const ManualFormsPanel = ({ dealId }) => {
  const [forms, setForms] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/bids/${dealId}/manual-forms`).then((r) => setForms(r.data.forms || []))
      .catch(() => setForms([])).finally(() => setLoading(false));
  }, [dealId]);

  useEffect(() => {
    load();
    const onRefresh = () => load();
    window.addEventListener("ironclad-refresh", onRefresh);
    return () => window.removeEventListener("ironclad-refresh", onRefresh);
  }, [load]);

  if (loading && forms === null) {
    return (
      <div className="mb-5 rounded-xl border border-zinc-200 bg-white px-4 py-3 flex items-center gap-2 text-sm text-zinc-500" data-testid="manual-forms-panel">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading manual forms…
      </div>
    );
  }
  if (!forms || forms.length === 0) return null;

  const pending = forms.filter((f) => f.status === "needs-input" || f.status === "needs-review").length;

  return (
    <div className="mb-5 rounded-xl border border-zinc-200 bg-white shadow-sm overflow-hidden" data-testid="manual-forms-panel">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-3 px-4 py-3 text-left" data-testid="manual-forms-toggle">
        <ClipboardList className="h-5 w-5 text-zinc-700 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-zinc-800">Manual-Form Completion</div>
          <div className="text-[11px] text-zinc-500 mt-0.5">
            Forms an operator completes by hand — download, fill, upload. Merged into the package on upload.
          </div>
        </div>
        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded border ${pending > 0 ? "bg-amber-100 text-amber-700 border-amber-300" : "bg-emerald-100 text-emerald-700 border-emerald-300"}`}
          data-testid="manual-forms-count">
          {pending > 0 ? `${pending} pending` : "All complete"}
        </span>
        {open ? <ChevronDown className="h-4 w-4 text-zinc-400" /> : <ChevronRight className="h-4 w-4 text-zinc-400" />}
      </button>

      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-zinc-200" data-testid="manual-forms-detail">
          <ul className="space-y-2 mt-2">
            {forms.map((f) => <FormRow key={f.form_key} dealId={dealId} form={f} onChanged={load} />)}
          </ul>
          <div className="text-[10px] text-zinc-400 mt-2">
            Uploading a completed form clears all its blockers together and merges it into the package;
            removing it restores them. Genuine reps stay a human confirm in SAM — never machine-completed.
          </div>
        </div>
      )}
    </div>
  );
};

export default ManualFormsPanel;
